import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, MenuItem, TextField,
} from '@mui/material';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ReactECharts from 'echarts-for-react';
import { v5Service } from '../services/v5Service';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

function StatCard({ title, value, suffix, alert }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 600, color: alert ? 'error.main' : 'inherit' }}>
          {value}{suffix && <Typography component="span" variant="subtitle2" color="text.secondary"> {suffix}</Typography>}
        </Typography>
      </CardContent>
    </Card>
  );
}

function Water() {
  const [summary, setSummary] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [summaryRows, txRows, facilityRows] = await Promise.all([
        v5Service.getWaterSummary(),
        v5Service.getWaterTransactions(orgId || null),
        v5Service.getWaterByFacility(orgId || null),
      ]);
      setSummary(summaryRows);
      setTransactions(txRows);
      setFacilities(facilityRows);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const totals = summary.reduce((acc, o) => ({
    withdrawal: acc.withdrawal + Number(o.withdrawal_m3),
    discharge: acc.discharge + Number(o.discharge_m3),
    recycle: acc.recycle + Number(o.recycle_m3),
    stressed: acc.stressed + Number(o.stressed_facilities),
  }), { withdrawal: 0, discharge: 0, recycle: 0, stressed: 0 });

  // ── chart data ─────────────────────────────────────────
  const monthly = {};
  const byCategory = {};
  transactions.forEach((t) => {
    const q = Number(t.quantity || 0);
    const month = String(t.transaction_date || '').slice(0, 7);
    if (month) {
      monthly[month] = monthly[month] || { withdrawal: 0, discharge: 0, recycle: 0 };
      const activity = String(t.activity_type_name || '').toLowerCase();
      if (activity.includes('withdrawal')) monthly[month].withdrawal += q;
      else if (activity.includes('discharge')) monthly[month].discharge += q;
      else if (activity.includes('recycl')) monthly[month].recycle += q;
    }
    const cat = t.category_name || 'Unknown';
    byCategory[cat] = (byCategory[cat] || 0) + q;
  });
  const months = Object.keys(monthly).sort();

  const monthlyChartOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (v) => `${Number(v).toLocaleString()} m³`,
    },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 8, right: 8, top: 24, bottom: 40, containLabel: true },
    xAxis: { type: 'category', data: months },
    yAxis: { type: 'value' },
    series: [
      { name: 'Withdrawal', type: 'line', smooth: true, symbolSize: 5, itemStyle: { color: '#1976d2' }, areaStyle: { opacity: 0.08 }, data: months.map((m) => monthly[m].withdrawal) },
      { name: 'Discharge', type: 'line', smooth: true, symbolSize: 5, itemStyle: { color: '#00838f' }, data: months.map((m) => monthly[m].discharge) },
      { name: 'Recycled', type: 'line', smooth: true, symbolSize: 5, itemStyle: { color: '#2e7d32' }, data: months.map((m) => monthly[m].recycle) },
    ],
  };

  const CATEGORY_COLORS = ['#1976d2', '#00838f', '#2e7d32', '#7b1fa2', '#f57c00', '#5d4037'];
  const categoryChartOption = {
    tooltip: {
      trigger: 'item',
      valueFormatter: (v) => `${Number(v).toLocaleString()} m³`,
    },
    legend: { bottom: 0, textStyle: { fontSize: 11 }, type: 'scroll' },
    series: [{
      type: 'pie',
      radius: ['42%', '68%'],
      center: ['50%', '42%'],
      label: { formatter: '{b}\n{d}%', fontSize: 10 },
      data: Object.entries(byCategory).map(([name, value], i) => ({
        name,
        value: Math.round(value),
        itemStyle: { color: CATEGORY_COLORS[i % CATEGORY_COLORS.length] },
      })),
    }],
  };

  // Clicking a category slice toggles a filter on the transactions table.
  const handleCategoryClick = (params) => {
    const name = typeof params?.name === 'string' ? params.name : '';
    if (!name) return;
    setCategoryFilter((prev) => (prev === name ? '' : name));
  };

  const visibleTransactions = categoryFilter
    ? transactions.filter((t) => t.category_name === categoryFilter)
    : transactions;

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WaterDropIcon color="primary" />
        <Typography variant="h4">Water</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        v5 water transactions (withdrawal, discharge, recycling) from the Open Footprint database
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <TextField
          select label="Organization" value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          sx={{ minWidth: 260 }} size="small"
        >
          <MenuItem value="">All organizations</MenuItem>
          {summary.map((o) => (
            <MenuItem key={o.organization_id} value={o.organization_id}>{o.organization_name}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><StatCard title="Total withdrawal" value={fmt(totals.withdrawal)} suffix="m³" /></Grid>
        <Grid item xs={6} md={3}><StatCard title="Total discharge" value={fmt(totals.discharge)} suffix="m³" /></Grid>
        <Grid item xs={6} md={3}><StatCard title="Recycled / reused" value={fmt(totals.recycle)} suffix="m³" /></Grid>
        <Grid item xs={6} md={3}><StatCard title="Stressed-site facilities" value={fmt(totals.stressed)} alert={totals.stressed > 0} /></Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Monthly Water Balance (m³)</Typography>
            <ReactECharts option={monthlyChartOption} style={{ height: 300 }} />
          </Paper>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Volume by Water Category
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                (click a slice to filter transactions)
              </Typography>
            </Typography>
            <ReactECharts option={categoryChartOption} style={{ height: 300 }} onEvents={{ click: handleCategoryClick }} />
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ p: 2, pb: 1 }}>Water by Organization (m³)</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {!orgId && <TableCell>Organization</TableCell>}
                <TableCell align="right">Withdrawal</TableCell>
                <TableCell align="right">Discharge</TableCell>
                <TableCell align="right">Recycled</TableCell>
                <TableCell align="right">Recycle %</TableCell>
                <TableCell align="right">Transactions</TableCell>
                <TableCell align="right">Stressed facilities</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary
                .filter((o) => !orgId || o.organization_id === orgId)
                .map((o) => {
                  const recyclePct = Number(o.withdrawal_m3) > 0
                    ? ((Number(o.recycle_m3) / Number(o.withdrawal_m3)) * 100).toFixed(1)
                    : '0.0';
                  return (
                    <TableRow key={o.organization_id} hover>
                      {!orgId && <TableCell>{o.organization_name}</TableCell>}
                      <TableCell align="right">{fmt(o.withdrawal_m3)}</TableCell>
                      <TableCell align="right">{fmt(o.discharge_m3)}</TableCell>
                      <TableCell align="right">{fmt(o.recycle_m3)}</TableCell>
                      <TableCell align="right">{recyclePct}</TableCell>
                      <TableCell align="right">{fmt(o.transactions)}</TableCell>
                      <TableCell align="right">
                        {Number(o.stressed_facilities) > 0
                          ? <Chip size="small" color="error" label={o.stressed_facilities} />
                          : 0}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {!summary.length && (
                <TableRow><TableCell colSpan={7}>No water transactions found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ p: 2, pb: 1 }}>Water by Facility (m³)</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {!orgId && <TableCell>Organization</TableCell>}
                <TableCell>Facility</TableCell>
                <TableCell align="right">Withdrawal</TableCell>
                <TableCell align="right">Discharge</TableCell>
                <TableCell align="right">Recycled</TableCell>
                <TableCell align="right">Recycle %</TableCell>
                <TableCell align="right">Transactions</TableCell>
                <TableCell>Stress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facilities.map((f) => {
                const recyclePct = Number(f.withdrawal_m3) > 0
                  ? ((Number(f.recycle_m3) / Number(f.withdrawal_m3)) * 100).toFixed(1)
                  : '0.0';
                return (
                  <TableRow key={f.facility_id} hover>
                    {!orgId && <TableCell>{f.organization_name}</TableCell>}
                    <TableCell>{f.facility_name}</TableCell>
                    <TableCell align="right">{fmt(f.withdrawal_m3)}</TableCell>
                    <TableCell align="right">{fmt(f.discharge_m3)}</TableCell>
                    <TableCell align="right">{fmt(f.recycle_m3)}</TableCell>
                    <TableCell align="right">{recyclePct}</TableCell>
                    <TableCell align="right">{fmt(f.transactions)}</TableCell>
                    <TableCell>
                      {f.water_stressed
                        ? <Chip size="small" color="error" label="Stressed" />
                        : <Chip size="small" color="success" variant="outlined" label="OK" />}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!facilities.length && (
                <TableRow><TableCell colSpan={8}>No facility data found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper>
        <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            Water Transactions {visibleTransactions.length >= 500 && <Typography component="span" variant="caption" color="text.secondary">(first 500)</Typography>}
          </Typography>
          {categoryFilter && (
            <Chip
              size="small" color="primary" label={`Category: ${categoryFilter}`}
              onDelete={() => setCategoryFilter('')}
            />
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                {!orgId && <TableCell>Organization</TableCell>}
                <TableCell>Facility</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Treatment</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Quantity (m³)</TableCell>
                <TableCell>Stress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTransactions.map((t) => (
                <TableRow key={t.water_transaction_id} hover>
                  <TableCell>{t.transaction_date}</TableCell>
                  {!orgId && <TableCell>{t.organization_name}</TableCell>}
                  <TableCell>{t.facility_name}</TableCell>
                  <TableCell>{t.activity_type_name}</TableCell>
                  <TableCell>{t.category_name}</TableCell>
                  <TableCell>{t.treatment_level_name || '—'}</TableCell>
                  <TableCell>{t.source_instance_name || '—'}</TableCell>
                  <TableCell align="right">{fmt(t.quantity)}</TableCell>
                  <TableCell>
                    {t.water_stressed
                      ? <Chip size="small" color="error" label="Stressed" />
                      : <Chip size="small" color="success" variant="outlined" label="OK" />}
                  </TableCell>
                </TableRow>
              ))}
              {!visibleTransactions.length && (
                <TableRow><TableCell colSpan={9}>No water transactions found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default Water;
